<template>
    <header class="header">
        <div class="header__inner">
            <router-link
                :to="getLocalizedPath('/')"
                class="brand"
            >
                <SmartIcon
                    icon="leaf"
                    size="24"
                    color="primary"
                />
                <div class="brand__text">
                    <span class="brand__name">Slothlike</span>
                    <span class="brand__subline">Efficient by nature 🦥</span>
                </div>
            </router-link>
            <nav class="nav">
                <router-link :to="getLocalizedPath('/')">{{ t('nav.home') }}</router-link>
                <router-link :to="getLocalizedPath('/services')">{{
                    t('nav.services')
                }}</router-link>
                <router-link :to="getLocalizedPath('/software')">{{
                    t('nav.software')
                }}</router-link>
                <router-link :to="getLocalizedPath('/faq')">{{ t('nav.faq') }}</router-link>
                <router-link :to="getLocalizedPath('/about')">{{ t('nav.about') }}</router-link>
                <router-link :to="getLocalizedPath('/contact')">{{ t('nav.contact') }}</router-link>
                <router-link :to="getLocalizedPath('/blog')">{{ t('nav.blog') }}</router-link>
            </nav>
            <div class="actions">
                <ThemeToggle />
                <LanguageSwitch />
                <v-btn
                    color="primary"
                    size="small"
                    rounded
                    class="cta-btn-desktop"
                    :to="getLocalizedPath('/contact')"
                >
                    {{ t('nav.contact') }}
                </v-btn>
                <v-btn
                    icon
                    variant="text"
                    class="burger-btn"
                    :aria-label="t('nav.menu', 'Menu')"
                    @click="mobileDrawer = true"
                >
                    <SmartIcon
                        icon="menu"
                        size="24"
                    />
                </v-btn>
            </div>
        </div>
    </header>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer
        v-model="mobileDrawer"
        location="right"
        temporary
        class="mobile-drawer"
    >
        <div class="mobile-drawer__header">
            <router-link
                :to="getLocalizedPath('/')"
                class="brand"
                @click="mobileDrawer = false"
            >
                <SmartIcon
                    icon="leaf"
                    size="24"
                    color="primary"
                />
                <span class="brand__name">Slothlike</span>
            </router-link>
            <v-btn
                icon
                variant="text"
                @click="mobileDrawer = false"
            >
                <SmartIcon
                    icon="x"
                    size="24"
                />
            </v-btn>
        </div>
        <v-divider />
        <v-list
            nav
            class="mobile-nav-list"
        >
            <v-list-item
                :to="getLocalizedPath('/')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="home"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.home') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/services')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="wrench"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.services') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/software')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="box"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.software') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/faq')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="help-circle"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.faq') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/about')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="info"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.about') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/contact')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="mail"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.contact') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
                :to="getLocalizedPath('/blog')"
                @click="mobileDrawer = false"
            >
                <template #prepend>
                    <SmartIcon
                        icon="layers"
                        size="20"
                    />
                </template>
                <v-list-item-title>{{ t('nav.blog') }}</v-list-item-title>
            </v-list-item>
        </v-list>
        <v-divider />
        <div class="mobile-drawer__footer">
            <v-btn
                color="primary"
                size="large"
                rounded
                block
                :to="getLocalizedPath('/contact')"
                @click="mobileDrawer = false"
            >
                {{ t('nav.contact') }}
            </v-btn>
        </div>
    </v-navigation-drawer>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import LanguageSwitch from './common/LanguageSwitch.vue'
    import ThemeToggle from './common/ThemeToggle.vue'
    import SmartIcon from './common/SmartIcon.vue'
    import { useTranslations } from '@/composables/useTranslations'
    import { useRoute } from 'vue-router'

    const { t, currentLanguage } = useTranslations()
    const route = useRoute()
    const mobileDrawer = ref(false)

    const getLocalizedPath = (path: string) => {
        // Get current language from route or default to currentLanguage
        const lang = (route.params.lang as string) || currentLanguage.value
        return `/${lang}${path === '/' ? '' : path}`
    }
</script>

<style scoped>
    .header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        background: rgba(var(--v-theme-surface), 0.7);
        backdrop-filter: blur(12px) saturate(180%);
        -webkit-backdrop-filter: blur(12px) saturate(180%);
        border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
        height: 60px; /* Explicit height to prevent CLS */
        contain: layout;
    }

    .header__inner {
        max-width: 1200px;
        margin: 0 auto;
        padding: 12px 24px;
        display: flex;
        align-items: center;
        gap: 24px;
    }

    .brand {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: inherit;
        transition: opacity 0.2s ease;
    }

    .brand:hover {
        opacity: 0.8;
    }

    .brand__text {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .brand__name {
        font-weight: 700;
        font-size: 1.1rem;
        line-height: 1.2;
        color: rgb(var(--v-theme-on-surface));
    }

    .brand__subline {
        font-size: 0.65rem;
        font-weight: 400;
        color: rgb(var(--v-theme-on-surface-variant));
        line-height: 1;
    }

    .nav {
        display: none;
        gap: 24px;
        margin-left: auto;
        contain: layout;
    }

    .nav a {
        color: rgb(var(--v-theme-on-surface));
        text-decoration: none;
        font-weight: 500;
        font-size: 0.95rem;
        transition: color 0.2s ease;
        position: relative;
    }

    .nav a:hover {
        color: rgb(var(--v-theme-primary));
    }

    .nav a.router-link-active::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 0;
        right: 0;
        height: 2px;
        background: rgb(var(--v-theme-primary));
        border-radius: 2px;
    }

    .actions {
        display: flex;
        gap: 8px;
        margin-left: auto;
        align-items: center;
    }

    .burger-btn {
        display: flex;
    }

    .cta-btn-desktop {
        display: none;
    }

    @media (min-width: 960px) {
        .nav {
            display: flex;
        }

        .actions {
            margin-left: 0;
        }

        .burger-btn {
            display: none;
        }

        .cta-btn-desktop {
            display: inline-flex;
        }
    }

    @media (max-width: 959px) {
        .header__inner {
            padding: 12px 16px;
        }

        .brand__text {
            display: none;
        }
    }

    /* Mobile Drawer Styles */
    .mobile-drawer__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
    }

    .mobile-drawer__header .brand {
        display: flex;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: inherit;
    }

    .mobile-drawer__header .brand__name {
        font-weight: 700;
        font-size: 1.1rem;
    }

    .mobile-nav-list {
        padding: 8px;
    }

    .mobile-nav-list :deep(.v-list-item) {
        border-radius: 8px;
        margin-bottom: 4px;
    }

    .mobile-nav-list :deep(.v-list-item__prepend) {
        margin-right: 12px;
    }

    .mobile-drawer__footer {
        padding: 16px;
    }
</style>
